import React, { useState, useCallback } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, AlertCircle, Loader2, Mail, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ============================================================
// ✅ SECURITY & VALIDATION HELPERS
// ============================================================

/**
 * Validates email format.
 */
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Sanitize general inputs (NOT passwords).
 * Prevents XSS if the input is reflected back from the server.
 */
const sanitizeInput = (input) => {
  if (!input) return "";
  return String(input)
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { forgotPassword, verifyOtp, resetPassword } = useAuth();

  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [resetToken, setResetToken] = useState(""); 

  // ---------------------------------------------------------
  // PULL TO REFRESH SETUP
  // ---------------------------------------------------------
  const dummyRefresh = useCallback(() => {
    console.log("Pull detected on static page.");
    return Promise.resolve();
  }, []);

  const containerRef = usePullToRefresh(dummyRefresh);

  // ---------------------------------------------------------
  // STEP 1: REQUEST CODE
  // ---------------------------------------------------------
  const handleRequestCode = async (e) => {
    e.preventDefault();
    
    // ✅ SECURITY: Validate Email Format
    if (!isValidEmail(email)) {
        setIsError(true);
        setMessage("Please enter a valid email address.");
        return;
    }

    setLoading(true);
    setMessage("");
    setIsError(false);

    try {
      // ✅ SECURITY: Sanitize email before sending to API (prevents injection in payload)
      const safeEmail = sanitizeInput(email);
      
      const result = await forgotPassword(safeEmail);
      if (result.success) {
        setMessage("If the account exists, a code has been sent to your email.");
        setIsError(false);
        setStep(2); 
      } else {
        setIsError(true);
        setMessage(result.error || "Failed to send request.");
      }
    } catch (err) {
      setIsError(true);
      setMessage("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // STEP 2: VERIFY OTP
  // ---------------------------------------------------------
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    // ✅ SECURITY: Ensure OTP is valid format (6 digits)
    if (otp.length !== 6) {
        setIsError(true);
        setMessage("Please enter the 6-digit code.");
        return;
    }

    setLoading(true);
    setMessage("");
    setIsError(false);

    try {
      // Sanitize email for the request
      const safeEmail = sanitizeInput(email);
      
      const result = await verifyOtp(safeEmail, otp);
      if (result.success) {
        setResetToken(result.token);
        setStep(3);
      } else {
        setIsError(true);
        setMessage(result.error || "Invalid code.");
      }
    } catch (err) {
      setIsError(true);
      setMessage("Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // STEP 3: RESET PASSWORD
  // ---------------------------------------------------------
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // ✅ SECURITY: Password Strength Validation
    if (newPassword.length < 6) {
      setIsError(true);
      setMessage("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setMessage("");
    setIsError(false);

    try {
      // ⚠️ DO NOT SANITIZE PASSWORD. It changes the user's secret.
      // Send it exactly as typed (HTTPS handles encryption).
      const result = await resetPassword(resetToken, newPassword);
      
      if (result.success) {
        setMessage("Password reset successful! Redirecting to login...");
        setIsError(false); 
        setTimeout(() => navigate("/auth"), 2000);
      } else {
        setIsError(true);
        setMessage(result.error || "Failed to reset password.");
      }
    } catch (err) {
      setIsError(true);
      setMessage("Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ SECURITY: Handler to force numeric input for OTP
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    setOtp(value);
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-slate-50 dark:bg-gray-900 flex p-4 font-sans transition-colors duration-300 overflow-x-hidden overflow-y-auto"
    >
      
      <div className="m-auto w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl dark:shadow-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8">
        
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <button 
            onClick={() => {
              if (step === 1) navigate("/auth");
              else setStep(step - 1);
            }} 
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {step === 1 ? "Forgot Password?" : step === 2 ? "Verify Code" : "New Password"}
          </h1>
          <div className="w-6"></div> 
        </div>

        {/* Progress Bar */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${s <= step ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
          ))}
        </div>

        {/* Message Alert */}
        {message && (
          <div className={`p-3 rounded-lg text-sm text-center mb-6 flex items-center justify-center gap-2 ${
            isError 
              ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30' 
              : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-500/30'
          }`}>
            {isError ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
            {message}
          </div>
        )}

        {/* STEP 1: Email Input */}
        {step === 1 && (
          <form onSubmit={handleRequestCode} className="space-y-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-indigo-500 dark:text-indigo-400" size={32} />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Enter your email address and we'll send you a code to reset your password.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())} // Trim whitespace
                placeholder="you@example.com"
                required
                // ✅ SECURITY: Add autoComplete
                autoComplete="email"
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <button 
              type="submit" 
              disabled={loading} 
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-all shadow-md"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : "Send Code"}
            </button>
          </form>
        )}

        {/* STEP 2: OTP Input */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                 <Key size={32} className="text-indigo-500 dark:text-indigo-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Enter the 6-digit code sent to <span className="text-gray-900 dark:text-white font-semibold break-all">{email}</span>
              </p>
            </div>

            <input
              type="text"
              inputMode="numeric" // ✅ UX: Opens numeric keyboard on mobile
              value={otp}
              onChange={handleOtpChange} // ✅ SECURITY: Forces digits only
              placeholder="123456"
              maxLength={6}
              // ✅ SECURITY: Add autoComplete
              autoComplete="one-time-code"
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-center text-3xl tracking-widest placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />

            <button 
              type="button" 
              onClick={() => setStep(1)} 
              className="w-full text-indigo-500 dark:text-indigo-400 text-sm hover:underline mt-2"
            >
              Wrong email? Change it
            </button>

            <button 
              type="submit" 
              disabled={loading} 
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-all shadow-md"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : "Verify Code"}
            </button>
          </form>
        )}

        {/* STEP 3: New Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock size={32} className="text-green-500 dark:text-green-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Create a new secure password for your account.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                // ✅ SECURITY: Add autoComplete
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-all shadow-md"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}